import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Toolbar from './components/Toolbar'
import FormulaBar from './components/FormulaBar'
import Grid from './components/Grid'
import SheetTabs from './components/SheetTabs'
import ChatDock from './components/ChatDock'
import PdfDialog from './components/PdfDialog'
import PrintView from './components/PrintView'
import TemplateDialog from './components/TemplateDialog'
import type { PrintJob } from './components/PrintView'
import { DEFAULT_PDF_OPTIONS, pageMetrics } from './core/pdf'
import type { PdfOptions } from './core/pdf'
import { getEvaluator, useStore, usedRange } from './core/store'
import { setPdfOpener } from './core/tools'
import { rangeToA1 } from './core/a1'
import { isAccepted, parseFile } from './core/io'

export default function App() {
  const [toast, setToast] = useState('')
  const [dragging, setDragging] = useState(false)
  const [pdfOpen, setPdfOpen] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [pdfOptions, setPdfOptions] = useState<PdfOptions>(DEFAULT_PDF_OPTIONS)
  const [printJob, setPrintJob] = useState<PrintJob | null>(null)
  const dragDepth = useRef(0)

  const loadWorkbook = useStore((s) => s.loadWorkbook)
  const version = useStore((s) => s.version)
  const selRange = useStore((s) => s.selRange)
  const statusMsg = useStore((s) => s.statusMsg)
  const activeSheet = useStore((s) => s.activeSheet)

  const showToast = useCallback((m: string) => {
    setToast(m)
    window.setTimeout(() => setToast((cur) => (cur === m ? '' : cur)), 3200)
  }, [])

  // ── 전역 드래그 앤 드롭 ───────────────────────────────────
  useEffect(() => {
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return
      dragDepth.current++
      setDragging(true)
    }
    const onLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (!dragDepth.current) setDragging(false)
    }
    const onDrop = async (e: DragEvent) => {
      if (!e.dataTransfer?.files?.length) return
      e.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      const file = e.dataTransfer.files[0]
      if (!isAccepted(file.name)) return showToast(`지원하지 않는 형식: ${file.name}`)
      try {
        const next = parseFile(file.name, await file.arrayBuffer())
        loadWorkbook(next, file.name)
        showToast(`${file.name} 불러옴 · 시트 ${next.sheets.length}개`)
      } catch (err: any) {
        showToast(`파일을 읽지 못했습니다: ${err?.message ?? err}`)
      }
    }
    window.addEventListener('dragover', onOver)
    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [loadWorkbook, showToast])

  // AI 가 export_pdf 를 부르면 대화상자를 옵션 채워서 연다
  useEffect(() => {
    setPdfOpener((patch) => {
      setPdfOptions((o) => ({ ...o, ...patch }))
      setPdfOpen(true)
    })
  }, [])

  // ── PDF: 인쇄 DOM 을 붙인 뒤 실제 인쇄를 띄운다 ───────────
  useEffect(() => {
    if (!printJob) return
    const m = pageMetrics(printJob.options)
    const style = document.createElement('style')
    style.id = 'print-page-rule'
    style.textContent = `@page { size: ${m.sizeCss}; margin: ${m.marginMm}mm; }`
    document.head.appendChild(style)

    const done = () => {
      setPrintJob(null)
      setPdfOpen(false)
      style.remove()
      window.removeEventListener('afterprint', done)
    }
    window.addEventListener('afterprint', done)

    // 레이아웃이 실제로 반영된 뒤에 인쇄해야 zoom/페이지 나눔이 맞는다
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => window.print()))
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('afterprint', done)
      document.getElementById('print-page-rule')?.remove()
    }
  }, [printJob])

  // Ctrl+P 는 브라우저 기본 인쇄 대신 우리 대화상자로
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && !printJob) {
        e.preventDefault()
        setPdfOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [printJob])

  // ── 상태줄 집계 ───────────────────────────────────────────
  const sheet = activeSheet()
  const r = selRange()
  const stats = useMemo(() => {
    const ev = getEvaluator()
    let count = 0
    let numCount = 0
    let sum = 0
    const cap = 200_000
    if ((r.r2 - r.r1 + 1) * (r.c2 - r.c1 + 1) > cap) return null
    for (let row = r.r1; row <= r.r2; row++)
      for (let col = r.c1; col <= r.c2; col++) {
        const v = ev.raw(sheet.name, row, col)
        if (v === null || v === '') continue
        count++
        if (typeof v === 'number') {
          numCount++
          sum += v
        }
      }
    return { count, numCount, sum }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, sheet.id, r.r1, r.c1, r.r2, r.c2])

  const u = usedRange(sheet)
  const round6 = (n: number) => Math.round(n * 1e6) / 1e6

  return (
    <div className="app">
      <Toolbar
        onToast={showToast}
        onOpenPdf={() => setPdfOpen(true)}
        onOpenTemplates={() => setTemplateOpen(true)}
      />
      <FormulaBar />
      <div className="app-main">
        <div className="sheet-side">
          <Grid />
          <SheetTabs onToast={showToast} />
          <div className="statusbar">
            <span>
              선택 <b>{rangeToA1(r)}</b>
            </span>
            {stats && stats.count > 0 && (
              <>
                <span>
                  개수 <b>{stats.count}</b>
                </span>
                {stats.numCount > 0 && (
                  <>
                    <span>
                      합계 <b>{round6(stats.sum)}</b>
                    </span>
                    <span>
                      평균 <b>{round6(stats.sum / stats.numCount)}</b>
                    </span>
                  </>
                )}
              </>
            )}
            <span>
              시트 <b>{sheet.name}</b> · 사용범위 <b>{u ? rangeToA1(u) : '비어있음'}</b> · 셀{' '}
              <b>{Object.keys(sheet.cells).length}</b>
            </span>
            <span style={{ marginLeft: 'auto' }}>{statusMsg}</span>
          </div>
        </div>

        <ChatDock />
      </div>

      {dragging && (
        <div className="drop-overlay">
          <div className="drop-card">
            <h2>📥 여기에 놓으세요</h2>
            <p>.xlsx · .xlsm · .xls · .csv · .tsv · .json · .ods</p>
          </div>
        </div>
      )}

      {pdfOpen && !printJob && (
        <PdfDialog
          options={pdfOptions}
          onChange={(patch) => setPdfOptions((o) => ({ ...o, ...patch }))}
          onClose={() => setPdfOpen(false)}
          onPrint={() =>
            setPrintJob({
              options: pdfOptions,
              selection: r,
              selectionSheetId: sheet.id,
            })
          }
        />
      )}

      {templateOpen && <TemplateDialog onClose={() => setTemplateOpen(false)} onApplied={showToast} />}

      {printJob && <PrintView job={printJob} />}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
